import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import type { MaintenanceKind, MaintenanceRecordView } from '@lims/shared-types';
import { equipmentApi, maintenanceApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  Alert,
  DataTable,
  ErrorState,
  FilterBar,
  LoadingState,
  PageHeader,
  Panel,
  ReadOnlyField,
  SearchField,
  StatusBadge,
  useToast,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDate } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { ActDialog, type ActField } from '../quality/ActDialog.js';
import { browserToday, isolate, maintenanceStatusTone } from '../quality/operations-ui.js';
import { RegisterPanel } from '../reports/register-panel.js';

/**
 * Planned maintenance and fault reports (Phase 12, D167, D168).
 *
 * A REPORT IS NOT A SUSPENSION. Reporting a fault changes nothing about the
 * instrument; the page says so, and points at the instrument where a person
 * who manages equipment decides separately. Completing maintenance is not a
 * calibration.
 */

type Act = { readonly act: 'complete' | 'resolve' | 'cancel'; readonly row: MaintenanceRecordView };

function useActs(onDone: () => Promise<void>) {
  const { t } = useTranslation();
  const [act, setAct] = useState<Act | null>(null);
  const dialog = act
    ? act.act === 'complete'
      ? {
          title: t('maintenance.completeTitle'),
          hint: t('maintenance.notCalibration'),
          submit: t('maintenance.complete'),
          destructive: false,
          fields: [
            { name: 'performedOn', label: t('maintenance.performedOn'), kind: 'date', isRequired: true, initial: browserToday() },
            { name: 'outcomeNote', label: t('maintenance.outcomeNote'), kind: 'textarea', isRequired: true },
            { name: 'nextScheduledFor', label: t('maintenance.nextScheduledFor'), kind: 'date', description: t('maintenance.nextHint') },
          ] as ActField[],
          run: (v: Record<string, string | null>) =>
            maintenanceApi.complete(act.row.id, {
              performedOn: v.performedOn,
              outcomeNote: v.outcomeNote?.trim(),
              ...(v.nextScheduledFor ? { nextScheduledFor: v.nextScheduledFor } : {}),
            }),
        }
      : act.act === 'resolve'
        ? {
            title: t('faults.resolveTitle'),
            hint: undefined,
            submit: t('faults.resolve'),
            destructive: false,
            fields: [
              { name: 'resolvedOn', label: t('faults.resolvedOn'), kind: 'date', isRequired: true, initial: browserToday() },
              { name: 'resolutionNote', label: t('faults.resolutionNote'), kind: 'textarea', isRequired: true },
            ] as ActField[],
            run: (v: Record<string, string | null>) =>
              maintenanceApi.resolve(act.row.id, { resolvedOn: v.resolvedOn, resolutionNote: v.resolutionNote?.trim() }),
          }
        : {
            title: t('maintenance.cancelTitle'),
            hint: undefined,
            submit: t('maintenance.cancel'),
            destructive: true,
            fields: [{ name: 'reason', label: t('common.reason'), kind: 'textarea', isRequired: true }] as ActField[],
            run: (v: Record<string, string | null>) => maintenanceApi.cancel(act.row.id, v.reason?.trim() ?? ''),
          }
    : null;

  const element = dialog ? (
    <ActDialog
      isOpen
      onClose={() => setAct(null)}
      title={dialog.title}
      {...(dialog.hint ? { hint: dialog.hint } : {})}
      submitLabel={dialog.submit}
      destructive={dialog.destructive}
      fields={dialog.fields}
      onSubmit={async (values) => {
        await dialog.run(values);
        await onDone();
      }}
    />
  ) : null;
  return { setAct, element };
}

function ActButtons({ row, onAct }: { readonly row: MaintenanceRecordView; readonly onAct: (act: Act) => void }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  if (!can(PERMISSIONS.EQUIPMENT_MANAGE)) return null;
  return (
    <span className="flex flex-wrap justify-end gap-inline">
      {row.status === 'planned' ? (
        <ActionButton size="sm" priority="secondary" onPress={() => onAct({ act: 'complete', row })}>
          {t('maintenance.complete')}
        </ActionButton>
      ) : null}
      {row.status === 'reported' ? (
        <ActionButton size="sm" priority="secondary" onPress={() => onAct({ act: 'resolve', row })}>
          {t('faults.resolve')}
        </ActionButton>
      ) : null}
      {row.status === 'planned' || row.status === 'reported' ? (
        <ActionButton size="sm" priority="tertiary" onPress={() => onAct({ act: 'cancel', row })}>
          {t('maintenance.cancel')}
        </ActionButton>
      ) : null}
    </span>
  );
}

export function MaintenanceListPage({ kind }: { readonly kind: MaintenanceKind }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { show } = useToast();
  const locale = currentLocale();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const isFault = kind === 'fault';

  const list = useQuery({
    queryKey: ['maintenance', kind, { page, search }],
    queryFn: () => maintenanceApi.list({ kind, page, ...(search ? { search } : {}) }),
  });
  const instruments = useQuery({
    queryKey: ['equipment', 'selectable'],
    queryFn: () => equipmentApi.list({ pageSize: 100 }),
    enabled: creating,
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['maintenance'] });
    show({ tone: 'success', message: t('maintenance.saved') });
  };
  const acts = useActs(refresh);

  const canCreate = isFault ? can(PERMISSIONS.EQUIPMENT_REPORT_FAULT) : can(PERMISSIONS.EQUIPMENT_MANAGE);
  const date = (value: string | null) => (value ? (formatDate(value, { locale }) ?? value) : t('common.notSet'));

  const columns: readonly DataColumn<MaintenanceRecordView>[] = [
    {
      key: 'equipment',
      header: t('maintenance.equipment'),
      render: (row) => (
        <Link className="text-primary-800 underline" to={`/equipment/${row.equipment.id}`} dir="ltr">
          {row.equipment.equipmentCode}
        </Link>
      ),
    },
    { key: 'description', header: t('maintenance.description'), render: (row) => row.description },
    {
      key: 'date',
      header: isFault ? t('faults.reportedOn') : t('maintenance.scheduledFor'),
      render: (row) => date(isFault ? row.reportedOn : row.scheduledFor),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (row) => (
        <span className="flex flex-wrap gap-inline">
          <StatusBadge label={t(`maintenanceStatus.${row.status}`)} tone={maintenanceStatusTone(row.status)} />
          {row.isOverdue ? <StatusBadge label={t('maintenance.overdue')} tone="danger" /> : null}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (row) => (
        <span className="flex flex-wrap justify-end gap-inline">
          <ActButtons row={row} onAct={acts.setAct} />
          <ActionButton priority="tertiary" size="sm" onPress={() => navigate(`/maintenance/${row.id}`)}>
            {t('common.open')}
          </ActionButton>
        </span>
      ),
    },
  ];

  const createFields: ActField[] = [
    {
      name: 'equipmentId',
      label: t('maintenance.equipment'),
      kind: 'select',
      isRequired: true,
      options: (instruments.data?.data ?? [])
        .filter((item) => item.status !== 'retired')
        .map((item) => ({ value: item.id, label: item.equipmentCode })),
    },
    { name: 'description', label: t('maintenance.description'), kind: 'textarea', isRequired: true },
    ...(isFault ? [] : [{ name: 'scheduledFor', label: t('maintenance.scheduledFor'), kind: 'date' as const, isRequired: true }]),
  ];

  return (
    <>
      <PageHeader
        title={isFault ? t('faults.title') : t('maintenance.title')}
        subtitle={isFault ? t('faults.subtitle') : t('maintenance.subtitle')}
        breadcrumbs={[{ label: isFault ? t('nav.faults') : t('nav.maintenance') }]}
        actions={
          canCreate ? (
            <ActionButton priority="primary" onPress={() => setCreating(true)}>
              {isFault ? t('faults.report') : t('maintenance.schedule')}
            </ActionButton>
          ) : null
        }
      />
      {isFault ? <Alert tone="info" title={t('faults.notSuspension')} /> : null}
      <div className="mt-stack">
        <RegisterPanel
          toolbar={
            <FilterBar>
              <SearchField
                value={search}
                onChange={(value) => {
                  setSearch(value);
                  setPage(1);
                }}
                placeholder={t('maintenance.searchPlaceholder')}
              />
            </FilterBar>
          }
        >
          <DataTable
            label={isFault ? t('faults.title') : t('maintenance.title')}
            columns={columns}
            rows={list.data?.data ?? []}
            rowKey={(row) => row.id}
            isLoading={list.isLoading}
            error={list.error}
            onRetry={() => void list.refetch()}
            emptyContent={isFault ? t('faults.noneYet') : t('maintenance.noneYet')}
            page={page}
            totalPages={list.data?.meta.totalPages ?? 1}
            onPageChange={setPage}
          />
        </RegisterPanel>
      </div>
      <ActDialog
        isOpen={creating}
        onClose={() => setCreating(false)}
        title={isFault ? t('faults.reportTitle') : t('maintenance.scheduleTitle')}
        hint={isFault ? t('faults.notSuspension') : t('maintenance.notCalibration')}
        submitLabel={isFault ? t('faults.report') : t('maintenance.schedule')}
        fields={createFields}
        onSubmit={async (values) => {
          if (isFault) {
            await maintenanceApi.reportFault({ equipmentId: values.equipmentId, description: values.description?.trim() });
          } else {
            await maintenanceApi.schedule({
              equipmentId: values.equipmentId,
              description: values.description?.trim(),
              scheduledFor: values.scheduledFor,
            });
          }
          await refresh();
        }}
      />
      {acts.element}
    </>
  );
}

/** One entry — the page a maintenance or fault notification leads to. */
export function MaintenanceRecordPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { show } = useToast();
  const locale = currentLocale();
  const entry = useQuery({ queryKey: ['maintenance', 'one', id], queryFn: () => maintenanceApi.get(id) });
  const acts = useActs(async () => {
    await queryClient.invalidateQueries({ queryKey: ['maintenance'] });
    show({ tone: 'success', message: t('maintenance.saved') });
  });

  if (entry.isLoading) return <LoadingState label={t('common.loading')} />;
  if (entry.error) return <ErrorState error={entry.error} onRetry={() => void entry.refetch()} />;
  const row = entry.data;
  if (!row) return null;
  const isFault = row.kind === 'fault';
  const date = (value: string | null) => (value ? (formatDate(value, { locale }) ?? value) : t('common.notSet'));

  return (
    <>
      <PageHeader
        title={`${row.equipment.equipmentCode} — ${t(`maintenanceKind.${row.kind}`)}`}
        subtitle={row.description}
        breadcrumbs={[
          { label: isFault ? t('nav.faults') : t('nav.maintenance'), to: isFault ? '/maintenance/faults' : '/maintenance' },
          { label: row.equipment.equipmentCode },
        ]}
        actions={<ActButtons row={row} onAct={acts.setAct} />}
      />
      {isFault ? <Alert tone="info" title={t('faults.notSuspension')} /> : null}
      <div className="mt-stack">
        <Panel title={t('maintenance.detailTitle')}>
          <div className="flex flex-col gap-stack">
            <ReadOnlyField label={t('common.status')}>
              <StatusBadge label={t(`maintenanceStatus.${row.status}`)} tone={maintenanceStatusTone(row.status)} />
            </ReadOnlyField>
            <ReadOnlyField label={t('maintenance.equipment')}>
              <Link className="text-primary-800 underline" to={`/equipment/${row.equipment.id}`} dir="ltr">
                {row.equipment.equipmentCode}
              </Link>
              <span className="ms-2">{t(`equipmentStatus.${row.equipment.status}`)}</span>
            </ReadOnlyField>
            {isFault ? (
              <ReadOnlyField label={t('faults.reportedOn')}>
                {t('maintenance.byOn', { name: isolate(row.reportedBy?.displayName), date: isolate(date(row.reportedOn)) })}
              </ReadOnlyField>
            ) : (
              <ReadOnlyField label={t('maintenance.scheduledFor')}>{date(row.scheduledFor)}</ReadOnlyField>
            )}
            {row.performedOn ? (
              <ReadOnlyField label={t('maintenance.performedOn')}>
                {t('maintenance.byOn', { name: isolate(row.performedBy?.displayName), date: isolate(date(row.performedOn)) })}
                <span className="block whitespace-pre-wrap" dir="auto">{row.outcomeNote}</span>
              </ReadOnlyField>
            ) : null}
            {row.resolvedOn ? (
              <ReadOnlyField label={t('faults.resolvedOn')}>
                {t('maintenance.byOn', { name: isolate(row.resolvedBy?.displayName), date: isolate(date(row.resolvedOn)) })}
                <span className="block whitespace-pre-wrap" dir="auto">{row.resolutionNote}</span>
              </ReadOnlyField>
            ) : null}
            {row.cancellationReason ? (
              <ReadOnlyField label={t('common.reason')}>{row.cancellationReason}</ReadOnlyField>
            ) : null}
          </div>
        </Panel>
      </div>
      {acts.element}
    </>
  );
}
